import { useCallback, useContext, useState } from 'react';

import api, { FetchError } from '../../common/api/api';
import { apiResolve } from '../../common/api/apiResolve';
import { useRecaptchaEnterprise } from '../../hooks/recaptcha/useRecaptchaEnterprise';
import { LoginContext } from '../../login/Login.context';
import { JwtToken } from '../../user/JwtToken.interface';
import { useUserContext } from '../../user/User.context';
import { GoogleLoginDto } from './GoogleAuth.interface';

function googleLogin(dto: GoogleLoginDto, recaptcha: string) {
  const request = api.post('users/login/google', dto, { recaptcha });
  return { request, result: apiResolve<JwtToken>(request) };
}

function googleSign(dto: GoogleLoginDto) {
  const request = api.post('users/sign/google', dto);
  return { request, result: apiResolve<JwtToken>(request) };
}

export function useGoogleLogin() {
  const [state, setState] = useState<{
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });
  const { setUser } = useUserContext();
  const { setDidLogout } = useContext(LoginContext);
  const {
    loadingRecaptchaToken,
    loadingRecaptchaTokenError,
    getRecaptchaToken
  } = useRecaptchaEnterprise();

  const login = useCallback(
    async (dto: GoogleLoginDto) => {
      setState(state => ({ ...state, isLoading: true, error: undefined }));
      // get recaptcha token and login
      const recaptcha = await getRecaptchaToken('login');
      const { result } = googleLogin(dto, recaptcha);
      // set user
      const { data, error } = await result;
      if (data) {
        api.setAccessToken(data.accessToken);
        setUser(data.user);
        setDidLogout(false);
      }
      setState(state => ({ ...state, isLoading: false, error }));
      return { data, error };
    },
    [getRecaptchaToken, setDidLogout, setUser]
  );

  return {
    login,
    isLoading: state.isLoading || loadingRecaptchaToken,
    error: state.error || loadingRecaptchaTokenError
  };
}

export function useGoogleSign() {
  const [state, setState] = useState<{
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });

  const sign = useCallback(async (dto: GoogleLoginDto) => {
    setState(state => ({ ...state, isLoading: true, error: undefined }));
    const { result } = googleSign(dto);
    const { data, error } = await result;
    setState(state => ({ ...state, isLoading: false, error }));
    return { data, error };
  }, []);

  return { ...state, sign };
}
